import { useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

const formatOptions: Intl.NumberFormatOptions = {
  useGrouping: false,
  maximumFractionDigits: 100,
};
const isPartialDecimal = (value: string) => /^[+-]?\d*(?:[.,]\d*)?$/.test(value);
const insertedValue = (input: HTMLInputElement, text: string) =>
  input.value.slice(0, input.selectionStart ?? 0) +
  text +
  input.value.slice(input.selectionEnd ?? input.value.length);
/** A saved number as the reader types it: "1,5" in French, "1.5" in English. */
export function formatDecimalForInput(value: number | undefined, locale: string): string {
  return value === undefined ? '' : new Intl.NumberFormat(locale, formatOptions).format(value);
}
const decimalLocale = (value: string) =>
  value.includes(',') ? 'fr-FR' : value.includes('.') ? 'en-US' : undefined;

/** Opt-in: select the parser before Chakra validates the next decimal character. */
export function useDecimalInputLocale(initialLocale?: string, allowSign = true) {
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState(initialLocale);
  const currentLocale = locale ?? i18n.resolvedLanguage ?? i18n.language;
  const prepare = (input: HTMLInputElement, text: string) => {
    const candidate = insertedValue(input, text);
    return {
      valid:
        isPartialDecimal(candidate) &&
        (allowSign || !/^[+-]/.test(candidate)) &&
        (!/\d/.test(candidate) || Number.isFinite(Number(candidate.replace(',', '.')))),
      locale: decimalLocale(candidate),
    };
  };
  return {
    rootProps: { locale: currentLocale, formatOptions },
    resetLocale: setLocale,
    inputProps: {
      onKeyDownCapture(event: KeyboardEvent<HTMLInputElement>) {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (event.key.length !== 1) {
          if (event.key === 'Backspace' || event.key === 'Delete') {
            const next = decimalLocale(event.currentTarget.value);
            if (next && next !== currentLocale) setLocale(next);
          }
          return;
        }
        const next = prepare(event.currentTarget, event.key);
        if (!next.valid) event.preventDefault();
        else if (next.locale && next.locale !== currentLocale) setLocale(next.locale);
      },
      onPaste(event: ClipboardEvent<HTMLInputElement>) {
        const next = prepare(event.currentTarget, event.clipboardData.getData('text'));
        if (!next.valid) event.preventDefault();
        else if (next.locale && next.locale !== currentLocale) setLocale(next.locale);
      },
      onBeforeInputCapture(event: FormEvent<HTMLInputElement> & { data?: string }) {
        const text = event.data ?? (event.nativeEvent as InputEvent).data;
        if (typeof text !== 'string') return;
        const next = prepare(event.currentTarget, text);
        if (!next.valid) event.preventDefault();
        // Software keyboards may emit beforeinput without a preceding keydown.
        else if (next.locale && next.locale !== currentLocale)
          flushSync(() => setLocale(next.locale));
      },
    },
  };
}

/** Keep partial text distinct from numeric parent echoes and explicit external restores. */
export function useDecimalNumberInput(
  value: number | undefined,
  onChange: (value: number | undefined) => void
) {
  const decimal = useDecimalInputLocale();
  const [raw, setRaw] = useState(() => formatDecimalForInput(value, decimal.rootProps.locale));
  const incoming = useRef(value);
  const pending = useRef<(number | undefined)[]>([]);
  if (!Object.is(value, incoming.current)) {
    incoming.current = value;
    const echo = pending.current.indexOf(value);
    if (echo >= 0) pending.current.splice(0, echo + 1);
    else {
      pending.current = [];
      setRaw(formatDecimalForInput(value, decimal.rootProps.locale));
    }
  }
  return {
    inputProps: decimal.inputProps,
    rootProps: {
      ...decimal.rootProps,
      value: raw,
      onValueChange(details: { value: string; valueAsNumber: number }) {
        setRaw(details.value);
        const next = Number.isFinite(details.valueAsNumber) ? details.valueAsNumber : undefined;
        const latest = pending.current.length ? pending.current.at(-1) : value;
        if (!Object.is(next, latest)) {
          pending.current.push(next);
          onChange(next);
        }
      },
    },
  };
}
